/* ══════════════════════════════════════════════════════════════
   India State Map — Choropleth state polygons + marker fallback
   Reuses World Monitor visual language for India map intelligence
   ══════════════════════════════════════════════════════════════ */

import { INDIA_STATES, getStateByCode, type StateData } from '../config/india-states';

export type IndiaRegionFilter = 'all' | 'north' | 'south' | 'east' | 'west' | 'central' | 'northeast';

export interface IndiaStateMapOptions {
  selectedCode?: string;
  region?: IndiaRegionFilter;
  onSelectState?: (stateCode: string) => void;
}

interface GeoJsonFeature {
  type: 'Feature';
  properties?: Record<string, unknown>;
  geometry?: unknown;
}

interface GeoJsonFeatureCollection {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
}

type LeafletBounds = unknown;

type LeafletMap = {
  setView(center: [number, number], zoom: number): LeafletMap;
  fitBounds(bounds: LeafletBounds, opts?: Record<string, unknown>): void;
  on(event: string, handler: (e: { latlng?: { lat: number; lng: number } }) => void): void;
  remove(): void;
};

type LeafletLayerGroup = {
  clearLayers(): void;
  addTo(map: LeafletMap): LeafletLayerGroup;
};

type LeafletInteractiveLayer = {
  feature?: GeoJsonFeature;
  bindPopup(content: string): void;
  on(event: string, handler: () => void): void;
  setStyle?(style: Record<string, unknown>): void;
  bringToFront?(): void;
  getBounds?(): LeafletBounds;
};

type LeafletMarker = {
  addTo(layer: LeafletLayerGroup): LeafletMarker;
  bindPopup(content: string): LeafletMarker;
  on(event: string, handler: () => void): void;
};

type LeafletGeoLayer = {
  addTo(map: LeafletMap): LeafletGeoLayer;
  remove(): void;
};

type LeafletLib = {
  map(el: HTMLElement, opts: Record<string, unknown>): LeafletMap;
  tileLayer(url: string, opts: Record<string, unknown>): { addTo(map: LeafletMap): void };
  layerGroup(): LeafletLayerGroup;
  circleMarker(latlng: [number, number], opts: Record<string, unknown>): LeafletMarker;
  geoJSON(
    data: GeoJsonFeatureCollection,
    opts: {
      style?: (feature: GeoJsonFeature) => Record<string, unknown>;
      onEachFeature?: (feature: GeoJsonFeature, layer: LeafletInteractiveLayer) => void;
    }
  ): LeafletGeoLayer;
};

const REGION_CODES: Record<Exclude<IndiaRegionFilter, 'all'>, string[]> = {
  north: ['DL', 'PB', 'HR', 'HP', 'JK', 'LA', 'UK', 'UP'],
  south: ['KA', 'TN', 'KL', 'TG', 'AP'],
  east: ['WB', 'OR', 'BR', 'JH'],
  west: ['MH', 'GJ', 'RJ', 'GA'],
  central: ['MP', 'CT'],
  northeast: ['AS', 'AR', 'MN', 'ML', 'MZ', 'NL', 'SK', 'TR'],
};

const STATE_GEOJSON_URLS = [
  '/data/india-states.geojson',
  'https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@ef25ebc/geojson/india.geojson',
];

const STATE_NAME_ALIASES: Record<string, string> = {
  orissa: 'OR',
  odisha: 'OR',
  telengana: 'TG',
  telangana: 'TG',
  nctofdelhi: 'DL',
  delhinct: 'DL',
  jammuandkashmir: 'JK',
  jammuandkashmirut: 'JK',
  uttaranchal: 'UK',
};

let leafletLoaded = false;
let mapInstance: LeafletMap | null = null;
let markersLayer: LeafletLayerGroup | null = null;
let geoLayer: LeafletGeoLayer | null = null;
let mapRoot: HTMLElement | null = null;
let stateGeoJsonCache: GeoJsonFeatureCollection | null = null;
let stateGeoJsonLoading: Promise<GeoJsonFeatureCollection | null> | null = null;
let geoJsonUnavailable = false;
let renderSequence = 0;

let activeOptions: Required<Omit<IndiaStateMapOptions, 'onSelectState'>> & { onSelectState?: (stateCode: string) => void } = {
  selectedCode: 'MH',
  region: 'all',
};

const CODE_BY_NORMALIZED_NAME = new Map<string, string>();
for (const state of INDIA_STATES) {
  CODE_BY_NORMALIZED_NAME.set(normalizeName(state.name), state.code);
}
for (const [alias, code] of Object.entries(STATE_NAME_ALIASES)) {
  CODE_BY_NORMALIZED_NAME.set(alias, code);
}

function normalizeName(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');
}

function loadLeaflet(): Promise<LeafletLib> {
  if (leafletLoaded && (window as unknown as Record<string, unknown>).L) {
    return Promise.resolve((window as unknown as Record<string, unknown>).L as LeafletLib);
  }

  return new Promise((resolve, reject) => {
    const cssId = 'imm-leaflet-css';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    const scriptId = 'imm-leaflet-js';
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existingScript && (window as unknown as Record<string, unknown>).L) {
      leafletLoaded = true;
      resolve((window as unknown as Record<string, unknown>).L as LeafletLib);
      return;
    }

    const script = existingScript || document.createElement('script');
    script.id = scriptId;
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      leafletLoaded = true;
      resolve((window as unknown as Record<string, unknown>).L as LeafletLib);
    };
    script.onerror = () => reject(new Error('Failed to load Leaflet'));
    if (!existingScript) document.head.appendChild(script);
  });
}

function getStatesByRegion(region: IndiaRegionFilter): StateData[] {
  if (region === 'all') return INDIA_STATES;
  const allow = new Set(REGION_CODES[region]);
  return INDIA_STATES.filter(s => allow.has(s.code));
}

function gdpColor(gdpBillionUSD: number): string {
  if (gdpBillionUSD >= 220) return '#00ff41';
  if (gdpBillionUSD >= 120) return '#00e5ff';
  if (gdpBillionUSD >= 60) return '#e5c100';
  return '#ff8c00';
}

function fmtPopulationCrore(populationMillion: number): string {
  return `${(populationMillion / 10).toFixed(1)} Cr`;
}

function popupHtml(state: StateData): string {
  const industries = state.keyIndustries.slice(0, 3).join(' • ');
  return `
    <div class="wm-popup">
      <div class="wm-popup__title">${state.name}</div>
      <div class="wm-popup__meta">
        <span class="wm-popup__source">${state.capital}</span>
        <span>GDP: $${state.gdpBillionUSD}B</span>
      </div>
      <div style="font-size:10px;color:var(--wm-text-dim);line-height:1.4;">
        Population: ${fmtPopulationCrore(state.population)}<br/>
        Key sectors: ${industries}
      </div>
    </div>
  `;
}

function featureStateName(feature: GeoJsonFeature): string {
  const props = feature.properties || {};
  const keys = ['st_nm', 'ST_NM', 'state', 'STATE', 'name', 'NAME_1', 'NAME', 'ut_name', 'UT_NAME'];
  for (const key of keys) {
    const value = props[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return '';
}

function featureStateCode(feature: GeoJsonFeature): string | null {
  const name = featureStateName(feature);
  if (!name) return null;
  const normalized = normalizeName(name);
  return CODE_BY_NORMALIZED_NAME.get(normalized) || null;
}

function updateCoords(lat: number, lng: number): void {
  const latEl = document.getElementById('im-coord-lat');
  const lngEl = document.getElementById('im-coord-lng');
  if (latEl) latEl.textContent = lat.toFixed(2);
  if (lngEl) lngEl.textContent = lng.toFixed(2);
}

function renderLegend(states: StateData[], mode: 'CHOROPLETH' | 'MARKERS'): void {
  const legend = document.getElementById('im-map-legend');
  if (!legend) return;
  const totalGdp = states.reduce((sum, s) => sum + s.gdpBillionUSD, 0);
  legend.innerHTML = `
    <div class="im-map-legend__title">INDIA STATE INTEL</div>
    <div class="im-map-legend__line">${states.length} regions visible</div>
    <div class="im-map-legend__line">Aggregate GDP: $${Math.round(totalGdp)}B</div>
    <div class="im-map-legend__line">Mode: ${mode}</div>
  `;
}

function stateStyle(code: string, mode: 'default' | 'hover' | 'selected'): Record<string, unknown> {
  const state = getStateByCode(code);
  const fill = gdpColor(state?.gdpBillionUSD || 0);
  if (mode === 'selected') {
    return {
      color: '#f8fff8',
      weight: 2.2,
      fillColor: fill,
      fillOpacity: 0.62,
    };
  }
  if (mode === 'hover') {
    return {
      color: '#b5ffc8',
      weight: 1.5,
      fillColor: fill,
      fillOpacity: 0.5,
    };
  }
  return {
    color: '#295029',
    weight: 0.9,
    fillColor: fill,
    fillOpacity: 0.38,
  };
}

async function loadStateGeoJson(): Promise<GeoJsonFeatureCollection | null> {
  if (stateGeoJsonCache) return stateGeoJsonCache;
  if (geoJsonUnavailable) return null;
  if (stateGeoJsonLoading) return stateGeoJsonLoading;

  stateGeoJsonLoading = (async () => {
    for (const url of STATE_GEOJSON_URLS) {
      try {
        const response = await fetch(url, { cache: 'force-cache' });
        if (!response.ok) continue;
        const data = await response.json() as Partial<GeoJsonFeatureCollection>;
        if (data.type === 'FeatureCollection' && Array.isArray(data.features) && data.features.length > 0) {
          const normalized: GeoJsonFeatureCollection = {
            type: 'FeatureCollection',
            features: data.features as GeoJsonFeature[],
          };
          stateGeoJsonCache = normalized;
          return normalized;
        }
      } catch {
        // try next source
      }
    }
    geoJsonUnavailable = true;
    return null;
  })();

  const result = await stateGeoJsonLoading;
  stateGeoJsonLoading = null;
  return result;
}

function clearGeoLayer(): void {
  if (geoLayer) {
    geoLayer.remove();
    geoLayer = null;
  }
}

function renderMarkersOnly(L: LeafletLib, states: StateData[]): void {
  if (!markersLayer) return;
  markersLayer.clearLayers();
  clearGeoLayer();

  const layer = markersLayer;
  for (const state of states) {
    const isSelected = state.code === activeOptions.selectedCode;
    const marker = L.circleMarker(state.geoCenter, {
      radius: isSelected ? 9 : 6,
      color: isSelected ? '#ffffff' : gdpColor(state.gdpBillionUSD),
      weight: isSelected ? 2 : 1,
      fillColor: gdpColor(state.gdpBillionUSD),
      fillOpacity: isSelected ? 0.9 : 0.72,
    });
    marker.bindPopup(popupHtml(state));
    marker.on('click', () => {
      if (activeOptions.onSelectState) activeOptions.onSelectState(state.code);
    });
    marker.addTo(layer);
  }

  renderLegend(states, 'MARKERS');
}

function renderChoropleth(L: LeafletLib, data: GeoJsonFeatureCollection, states: StateData[]): void {
  if (!markersLayer || !mapInstance) return;
  const allowedCodes = new Set(states.map(s => s.code));
  const selectedCode = activeOptions.selectedCode;

  markersLayer.clearLayers();
  clearGeoLayer();

  const matchedCodes = new Set<string>();
  const features: GeoJsonFeature[] = [];

  for (const feature of data.features) {
    const code = featureStateCode(feature);
    if (!code || !allowedCodes.has(code)) continue;
    matchedCodes.add(code);
    features.push({
      ...feature,
      properties: {
        ...(feature.properties || {}),
        __immCode: code,
      },
    });
  }

  if (features.length === 0) {
    renderMarkersOnly(L, states);
    return;
  }

  const featureCollection: GeoJsonFeatureCollection = {
    type: 'FeatureCollection',
    features,
  };

  geoLayer = L.geoJSON(featureCollection, {
    style: (feature) => {
      const code = feature.properties?.__immCode;
      if (typeof code !== 'string') return stateStyle('MH', 'default');
      if (code === selectedCode) return stateStyle(code, 'selected');
      return stateStyle(code, 'default');
    },
    onEachFeature: (feature, layer) => {
      const codeValue = feature.properties?.__immCode;
      if (typeof codeValue !== 'string') return;
      const state = getStateByCode(codeValue);
      if (!state) return;

      layer.bindPopup(popupHtml(state));
      layer.on('click', () => {
        if (activeOptions.onSelectState) activeOptions.onSelectState(codeValue);
      });
      layer.on('mouseover', () => {
        if (!layer.setStyle) return;
        const selected = codeValue === activeOptions.selectedCode;
        layer.setStyle(stateStyle(codeValue, selected ? 'selected' : 'hover'));
        if (layer.bringToFront) layer.bringToFront();
      });
      layer.on('mouseout', () => {
        if (!layer.setStyle) return;
        const selected = codeValue === activeOptions.selectedCode;
        layer.setStyle(stateStyle(codeValue, selected ? 'selected' : 'default'));
      });

    },
  });
  geoLayer.addTo(mapInstance);

  const unmatchedStates = states.filter(s => !matchedCodes.has(s.code));
  if (unmatchedStates.length > 0) {
    const markerLayer = markersLayer;
    for (const state of unmatchedStates) {
      const marker = L.circleMarker(state.geoCenter, {
        radius: state.code === selectedCode ? 8 : 5,
        color: '#d8ffe6',
        weight: 1,
        fillColor: gdpColor(state.gdpBillionUSD),
        fillOpacity: 0.8,
      });
      marker.bindPopup(popupHtml(state));
      marker.on('click', () => {
        if (activeOptions.onSelectState) activeOptions.onSelectState(state.code);
      });
      marker.addTo(markerLayer);
    }
  }

  const selectedState = getStateByCode(selectedCode);
  if (selectedState) {
    mapInstance.setView(selectedState.geoCenter, activeOptions.region === 'all' ? 5 : 5.8);
  }

  renderLegend(states, 'CHOROPLETH');
}

function ensureContainerShell(container: HTMLElement): void {
  if (container.querySelector('#im-leaflet-map')) return;
  container.innerHTML = `
    <div class="wm-map-container" id="im-leaflet-map">
      <div class="wm-loading">
        <div class="wm-loading__spinner"></div>
        Loading India state map...
      </div>
    </div>
    <div class="im-map-legend" id="im-map-legend"></div>
    <div class="wm-coords">
      LAT <span class="wm-coords__value" id="im-coord-lat">--</span><br/>
      LON <span class="wm-coords__value" id="im-coord-lng">--</span>
    </div>
  `;
}

async function refreshMapVisuals(sequence: number): Promise<void> {
  if (!mapInstance || !markersLayer) return;
  const states = getStatesByRegion(activeOptions.region);
  const L = await loadLeaflet();
  const geoJson = await loadStateGeoJson();
  if (sequence !== renderSequence) return;
  if (!mapInstance || !markersLayer) return;

  if (geoJson) {
    renderChoropleth(L, geoJson, states);
  } else {
    renderMarkersOnly(L, states);
  }
}

export function renderIndiaStateMap(container: HTMLElement, options: IndiaStateMapOptions = {}): void {
  if (mapRoot && mapRoot !== container) {
    destroyIndiaStateMap();
  }

  activeOptions = {
    selectedCode: options.selectedCode || activeOptions.selectedCode || 'MH',
    region: options.region || activeOptions.region || 'all',
    onSelectState: options.onSelectState || activeOptions.onSelectState,
  };

  if (!mapRoot) {
    mapRoot = container;
    ensureContainerShell(container);
  }

  if (mapRoot !== container) {
    mapRoot = container;
    ensureContainerShell(container);
  }

  loadLeaflet().then(L => {
    if (!mapRoot) return;
    const mapEl = mapRoot.querySelector('#im-leaflet-map') as HTMLElement | null;
    if (!mapEl) return;

    if (!mapInstance) {
      mapEl.innerHTML = '';
      mapInstance = L.map(mapEl, {
        zoomControl: true,
        attributionControl: true,
      }).setView([22.9, 79.2], 5);

      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 9,
        minZoom: 4,
        attribution: '&copy; OpenStreetMap &copy; CARTO',
      }).addTo(mapInstance);

      markersLayer = L.layerGroup().addTo(mapInstance);

      mapInstance.on('mousemove', e => {
        if (!e.latlng) return;
        updateCoords(e.latlng.lat, e.latlng.lng);
      });
    }

    renderSequence += 1;
    void refreshMapVisuals(renderSequence);
  }).catch(() => {
    const mapEl = mapRoot?.querySelector('#im-leaflet-map') as HTMLElement | null;
    if (mapEl) mapEl.innerHTML = '<div class="wm-empty">⚠ Failed to load India map.</div>';
  });
}

export function destroyIndiaStateMap(): void {
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }
  markersLayer = null;
  clearGeoLayer();
  mapRoot = null;
  renderSequence += 1;
}
